import React, {useEffect} from "react";
import Heading from "../../components/heading/Heading.tsx";
import AboutUs from "../../components/aboutUs/AboutUs.tsx";
import OurProjects from "../../components/our-projects/OurProjects.tsx";
import WorkStages from "../../components/work-stages/WorkStages.tsx";
import Sectors, { ISectorCard } from "../../components/sectors/Sectors.tsx";
import FormCard from "../../components/formCard/FormCard.tsx";
import noise from "../../media/noise.png";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import truckImage from "../../media/sectors/truck.webp";
import countingImage from "../../media/sectors/counting.webp";
import piggyBankImage from "../../media/sectors/piggy-bank.webp";
import radioTowerImage from "../../media/sectors/radio-tower.webp";
import roadImage from "../../media/sectors/road.webp";
import mayorOfficeImage from "../../media/projects/1551.webp";
import goGoBagImage from "../../media/projects/go-go-bag.webp";
import SyviImage from "../../media/projects/syvi.webp";
import eCherhaImage from "../../media/projects/e-queue.webp";
import eRoadImage from "../../media/projects/e-road.webp";
import cyberTrackerImage from "../../media/projects/cybertracker.webp";
import FrequentlyAskedQuestions, {
  IQuestion
} from "../../components/frequently-asked-questions/FrequentlyAskedQuestions.tsx";
import {IconGrid} from "../../components/iconGrid/IconGrid.tsx";
import Partners from "../../components/partners/Partners.tsx";
import {Testimonials} from "../../components/testimonials/Testimonials.tsx";
import OrganizationSeo from "../../components/Seo/OrganizationSeo.tsx";

const Home = () => {
  const { t } = useTranslation();

  const projects = {
    left: [
      {
        title: t("home.projects.project.1.title"),
        text: t("home.projects.project.1.info"),
        path: "/case-study/cybertracker",
        image: cyberTrackerImage,
        id: 2,
      },
      {
        title: t("home.projects.project.3.title"),
        text: t("home.projects.project.3.info"),
        path: "/case-study/1551",
        image: mayorOfficeImage,
        id: 4,
      },
      {
        title: t("home.projects.project.4.title"),
        text: t("home.projects.project.4.info"),
        path: "/case-study/go-go-bag",
        image: goGoBagImage,
        id: 5,
        isStartup: true

      },
    ],
    right: [
      {
        title: t("home.projects.project.0.title"),
        text: t("home.projects.project.0.info"),
        path: "/case-study/e-road",
        image: eRoadImage,
        id: 1,
      },
      {
        title: t("home.projects.project.2.title"),
        text: t("home.projects.project.2.info"),        
        path: "/case-study/e-cherha",
        image: eCherhaImage,
        id: 3,
      },
      {
        title: t("home.projects.project.5.title"),
        text: t("home.projects.project.5.info"),
        path: "/case-study/syvi",
        image: SyviImage,
        id: 6,
        isStartup: true
      },
    ],
  };

  const sectorCards: ISectorCard[] = [
    {
      image: truckImage,
      title: t("expertiseSectors.logistics.title"),
      text: t("expertiseSectors.logistics.description"),
      id: 2,
      link: '/industries/logistics-and-supply-chains'
    },
    {
      image: countingImage,
      title: t("expertiseSectors.utilitiesServices.title"),
      text: t("expertiseSectors.utilitiesServices.description"),
      id: 3,
      link: '/industries/public-sectors'
    },
    {
      image: piggyBankImage,
      title: t("expertiseSectors.financeAndBanking.title"),
      text: t("expertiseSectors.financeAndBanking.description"),
      id: 4,
      link: '/industries/finance-and-banking'
    },
    {
      image: radioTowerImage,
      title: t("expertiseSectors.telecommunications.title"),
      text: t("expertiseSectors.telecommunications.description"),
      id: 5,
      link: '/industries/telecommunications'
    },
    {
      image: roadImage,
      title: t("expertiseSectors.roadEngineering.title"),
      text: t("expertiseSectors.roadEngineering.description"),
      id: 6,
      link: '/industries/road-engineering'
    },
  ];

  const questions: { left: IQuestion[]; right: IQuestion[] } = {
    left: [
      {
        title: t("frequentlyAskedQuestions.blocks.0.title"),
        description: t("frequentlyAskedQuestions.blocks.0.body"),
        id: 1,
      },
      {
        title: t("frequentlyAskedQuestions.blocks.1.title"),
        description: t("frequentlyAskedQuestions.blocks.1.body"),
        id: 2,
      },
      {
        title: t("frequentlyAskedQuestions.blocks.2.title"),
        description: t("frequentlyAskedQuestions.blocks.2.body"),
        id: 3,
      },
    ],
    right: [
      {
        title: t("frequentlyAskedQuestions.blocks.3.title"),
        description: t("frequentlyAskedQuestions.blocks.3.body"),
        id: 4,
      },
      {
        title: t("frequentlyAskedQuestions.blocks.4.title"),
        description: t("frequentlyAskedQuestions.blocks.4.body"),
        id: 5,
      },
      {
        title: t("frequentlyAskedQuestions.blocks.5.title"),
        description: t("frequentlyAskedQuestions.blocks.5.body"),
        id: 6,
      },
      {
        title: t("frequentlyAskedQuestions.blocks.6.title"),
        description: t("frequentlyAskedQuestions.blocks.6.body"),
        id: 7,
      },
    ],
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
      <main style={{overflowX: 'hidden'}}>
        <Helmet>
          <title>
            Goteco: Secure and Scalable GovTech and Business Solutions
          </title>
          <meta
              name="description"
              content="Goteco specializes in developing secure, scalable systems that boost efficiency across public and private sectors, optimizing processes in manufacturing, e-commerce, and construction industries."
          />
          <link rel="canonical" href="https://go-te.co/" />
        </Helmet>

        <OrganizationSeo
            title="Goteco: Secure and Scalable GovTech and Business Solutions"
            description="Goteco specializes in developing secure, scalable systems that boost efficiency across public and private sectors, optimizing processes in manufacturing, e-commerce, and construction industries."
            url="https://go-te.co/"
            image="https://go-te.co/logo.svg"
        />

        <div className="App">
          <section className="section--1" id="nav-dark">
            <div className="circle circle--4"></div>
            <div className="circle circle--3"></div>
            <div className="circle circle--2"></div>
            <div className="circle circle--1"></div>
            <img src={noise} alt={"noise background"} className="noise"/>
            <Heading/>
          </section>

          <section className="section--white" id="nav-dark">
            <AboutUs
                aboutUsBlocks={t("about-us.information", { returnObjects: true })}
                header={t("about-us.header")}
                withHover={true}
            />
          </section>

          <section className="section--dark-gray" id="nav-light">
            <WorkStages/>
          </section>

          <section className="section--white p-84" id="nav-dark">
            <IconGrid/>
          </section>

          <section className="section--white" id="nav-dark">
            <OurProjects projects={projects}/>
          </section>

          <section className="section--light-gray p-80" id="nav-dark">
            <Partners/>
          </section>

          <section className="section--white" id="nav-dark">
            <Sectors title={t("expertiseSectors.title")} cards={sectorCards}/>
          </section>

          <section className="section--dark-gray" id="nav-light">
            <Testimonials/>
          </section>

          <section className="section--light-gray" id="nav-dark">
            <FrequentlyAskedQuestions title={t("frequentlyAskedQuestions.title")} questions={questions}/>
          </section>

          <section className="section--white" id="nav-dark">
            <FormCard id='contact'/>
          </section>
        </div>
      </main>
  );
};

export default Home;
